// Types and presentation rules for the task board.
//
// A plain .ts module with no JSX, so Node's own test runner can import it under
// --experimental-strip-types. The two things worth testing here are the group
// ordering and how a null on_time is rendered, and both are decisions that would
// otherwise be buried inside a component.
//
// NOTHING IS COMPUTED HERE. on_time, days_delta and is_overdue arrive already
// decided by public.v_task_board. This file only chooses colours and words for
// them. If a figure ever needs calculating, it belongs in the view, per the
// standing rule that domain logic lives in Postgres.

export type TaskStatus = 'Not Started' | 'In Progress' | 'Blocked' | 'Done' | 'Cancelled'
export type TaskPriority = 'High' | 'Medium' | 'Low'
/**
 * How much authority the owner of a task has.
 *
 * Hassan Osman, "Effective Delegation of Authority", step four of five. Added
 * 27 August 2026 with db/migrations/0085. NULL, not a fourth member of this
 * union, is what "not set" is, and it is the value every one of the 359 tasks on
 * this board started with: a meeting's notes do not record the authority that was
 * granted, so nothing is allowed to guess it.
 */
export type TaskAuthority = 'Recommend' | 'Inform and initiate' | 'Act'
export type BoardGroup =
  | 'Open'
  | 'Ongoing'
  | 'Blocked'
  | 'Standing'
  | 'Completed'
  | 'On-Hold'
  | 'Cancelled'

/** What a task can be linked to. */
export type LinkKind = 'vehicle' | 'owner' | 'driver'

/** One linked vehicle, vehicle owner or driver, as v_task_link labels it. */
export type TaskLink = {
  id: string
  label: string
  sub_label: string | null
  is_active: boolean
}

/** One row of the pickers. `status` is the record's own word for its state. */
export type TaskLinkOption = {
  kind: LinkKind
  id: string
  label: string
  sub_label: string | null
  is_active: boolean
  status: string | null
}

export type TaskRow = {
  id: string
  ref: number
  title: string
  description: string | null
  status: TaskStatus
  priority: TaskPriority | null
  board_group: BoardGroup
  parent_task_id: string | null
  source_meeting: string | null
  source_meeting_label: string | null
  ingest_run_id: string | null
  source_doc_url: string | null
  source_excerpt: string | null
  start_date: string | null
  target_completion: string | null
  actual_completion: string | null
  next_update: string | null
  duration_hours: string | number | null
  monday_item_id: string | null
  created_by: string | null
  created_at: string
  updated_by: string | null
  updated_at: string
  // Filtering only. Never read positionally against assignee_emails: the two
  // arrays are different lengths whenever a name has no address on record.
  // See db/migrations/0018_task_assignee_list.sql.
  assignee_names: string[]
  assignee_emails: string[]
  assignee_list: { name: string; email: string | null; person_id: string | null }[]
  assignee_unlinked: boolean
  update_count: number
  last_update_at: string | null
  subitem_count: number
  subitem_done_count: number
  // 'On Time' | 'Late' | 'Pending' | null. Null means NOT MEASURABLE, which is
  // a different thing from on time, and the screen must never round it up.
  on_time: string | null
  days_delta: number | null
  is_overdue: boolean
  is_open: boolean
  // The card is not in the group its status implies. 18 rows arrived from
  // monday.com in that state; db/migrations/0023 resolved all 18 by letting
  // status win, and this stays because a card can still be dragged to the wrong
  // group by hand tomorrow.
  group_status_conflict: boolean
  // EVERY FIELD BELOW IS OPTIONAL ON PURPOSE. The columns arrive with
  // db/migrations/0023 and 0025, and this page is deployed BEFORE those are
  // applied, so that no task can vanish from the board during the window
  // between the deploy and the migration. Every reader has to tolerate the
  // column being absent, which is what linksOf below is for.
  // Arrives with the notifications branch, 2 September 2026. Optional for the
  // same reason every field below is: the page can be deployed before the
  // handler that returns it, and a reader that cannot tolerate the column being
  // absent makes every task vanish from the board in the window between.
  //
  // UNDEFINED IS NOT FALSE. Undefined means nobody asked; false means this task
  // genuinely does not name you.
  mentions_me?: boolean | null
  restored_from_finished?: boolean | null
  regrouped_by?: string | null
  regrouped_at?: string | null
  // Arrives with db/migrations/0232, 16 September 2026. Optional for the same
  // reason every field around it is: the page can be deployed before the
  // migration, and a reader that cannot tolerate the column being absent makes
  // every task vanish from the board in the window between.
  //
  // origin_kind is which BOARD raised this task, which is a different thing from
  // source_doc_url. That one means the Gemini meeting notes and is drawn with
  // those words beside it.
  // 16 September 2026. False when this row came back only because something
  // else in its family matched the filter, so the counts can stay counts of
  // what was actually asked for.
  //
  // UNDEFINED IS NOT FALSE, and the distinction matters here more than most:
  // undefined means the handler did not report it, and treating that as "not
  // asked for" would empty the count strip on the first load after a deploy.
  matched_filter?: boolean | null
  origin_kind?: 'marketing' | 'tech' | 'dispute' | 'content-gap' | 'offboarding' | null
  origin_key?: string | null
  origin_url?: string | null
  // Finished, and no completion date anywhere. 25 rows on 16 September 2026, all
  // of them monday.com imports whose export carried no date either. Drawn as
  // "not recorded" rather than left blank, because a blank cell beside a Done
  // pill reads as a job somebody forgot rather than a fact nobody has.
  completion_date_unknown?: boolean | null
  vehicle_list?: TaskLink[] | null
  owner_list?: TaskLink[] | null
  driver_list?: TaskLink[] | null
  // Arrives with db/migrations/0084. Optional for the same reason every field
  // above it is: this page can be deployed before that migration is applied, and
  // a reader that cannot tolerate the column being absent makes every task
  // vanish from the board during the window in between.
  //
  // UNDEFINED IS NOT ZERO HERE. Undefined means the count was not asked for or
  // has not arrived; zero means this task genuinely has no files. The board
  // renders those two differently, because "no files" and "not known" are not the
  // same claim.
  attachment_count?: number | null
  // Arrive with db/migrations/0085. Optional for the same reason as every field
  // above: the bundle can be deployed before the migration is applied, and a
  // reader that cannot tolerate the column being absent empties the whole board
  // for the length of that window.
  //
  // NULL AUTHORITY IS A REAL ANSWER, not a missing one. It means nobody has said
  // how much authority the owner has, which is the state the board has been in
  // since it existed, and the screen says so in words rather than leaving the
  // cell blank.
  authority_level?: TaskAuthority | null
  delegation_gaps?: string[] | null
}

/**
 * One file attached to a task.
 *
 * There is no signed URL on this type on purpose. A panel listing twenty files
 * would mean twenty edge-function calls to render a list nobody has clicked in
 * yet, so a URL is signed one at a time, when a file is opened, by
 * tasks/getTaskAssetUrl.ts. A URL that is an hour old and was never used is also
 * a URL sitting in a browser's memory for no reason.
 */
export type TaskAsset = {
  id: string
  filename: string
  mime_type: string | null
  size_bytes: string | number | null
  uploaded_by: string
  uploaded_at: string
}

/** The linked records of one kind, tolerating a view that does not have them yet. */
export function linksOf(row: TaskRow, kind: LinkKind): TaskLink[] {
  const list =
    kind === 'vehicle' ? row.vehicle_list : kind === 'owner' ? row.owner_list : row.driver_list
  return Array.isArray(list) ? list : []
}

export type TaskPerson = {
  name: string
  person_id: string | null
  email: string | null
  has_login: boolean
  open_tasks: number
  overdue_tasks: number
  total_tasks: number
}

export type IngestRun = {
  id: string
  drive_file_id: string
  drive_file_title: string
  doc_url: string | null
  meeting_date: string | null
  status: string
  next_steps_found: number
  tasks_created: number
  tasks_linked: number
  error_text: string | null
  started_at: string
  finished_at: string | null
  undone_at: string | null
  undone_by: string | null
  source_label: string | null
  source_key: string | null
  /**
   * What the vehicle and owner matcher did, per next step. db/migrations/0086.
   *
   * Optional because this bundle can be deployed before that migration, and NULL
   * on every run that predates it, which is every run before 27 August 2026.
   */
  link_report?: IngestLinkReport[] | null
}

/** One next step's worth of vehicle and owner matching, as the run recorded it. */
export type IngestLinkReport = {
  task: string
  chased: boolean
  matched: { token: string; registration: string; how: string }[]
  /**
   * Tokens that look like a registration and were NOT linked, with the reason.
   *
   * This is the useful half. A plate the matcher refused is either a
   * mis-transcription or a car that is not on the register, and both are worth a
   * person's thirty seconds. The matcher will not guess between two vehicles.
   */
  unplaced: { token: string; reason: string; candidates?: string[] }[]
}

export type TaskMention = {
  person_id: string
  name: string
  /**
   * Whether a notification was queued for this person.
   *
   * False is a real state, not a failure to load: somebody on file with no
   * address anywhere is still legitimately mentioned, and the panel says so
   * rather than implying they were told. Delivery itself lives in
   * public.outbound_messages, keyed by the update id.
   */
  notified: boolean
}

export type TaskUpdateRow = {
  id: string
  parent_update_id: string | null
  author_name: string
  author_email: string | null
  body: string
  created_at: string
  // Optional because the panel is deployed before nothing in particular, but a
  // thread written before migration 0066 has no mention rows and older cached
  // responses may not carry the key at all.
  mentions?: TaskMention[] | null
}

export type TaskActivityRow = {
  field: string
  old_value: string | null
  new_value: string | null
  actor_email: string | null
  at: string
}

export const STATUS_OPTIONS: TaskStatus[] = [
  'Not Started',
  'In Progress',
  'Blocked',
  'Done',
  'Cancelled',
]

export const PRIORITY_OPTIONS: TaskPriority[] = ['High', 'Medium', 'Low']

// Osman's three levels, in escalating order of trust. The order is the point:
// read top to bottom it is a ladder somebody moves up as they master the work,
// which is what his third section is about.
export const AUTHORITY_OPTIONS: TaskAuthority[] = ['Recommend', 'Inform and initiate', 'Act']

/** What the board column shows. The full words do not fit in 112px. */
export const AUTHORITY_SHORT: Record<TaskAuthority, string> = {
  Recommend: 'Recommend',
  'Inform and initiate': 'Inform first',
  Act: 'Act',
}

/**
 * What each level actually obliges the two people to do, in plain words.
 *
 * On the picker and in the tooltip rather than in a policy document, because the
 * whole failure this field exists to prevent is two people assuming different
 * answers, and that failure happens at the moment somebody reads the label.
 */
export const AUTHORITY_HINT: Record<TaskAuthority, string> = {
  Recommend: 'Bring back a recommended course of action. The decision stays with the manager.',
  'Inform and initiate': 'Tell the manager before acting, then act.',
  Act: 'Full authority to act alone. No permission needed and no notice required.',
}

export const AUTHORITY_CLASS: Record<TaskAuthority, string> = {
  Recommend: 'bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-200',
  'Inform and initiate': 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
  Act: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200',
}

/**
 * The elements of a delegation a task is missing, as v_task_board names them.
 *
 * Empty on a finished task, by the view's own choice. These are the words shown
 * to a person, so they are the words a person would use: "nobody owns it", not
 * "assignee_names is null".
 */
export const DELEGATION_GAP_LABEL: Record<string, string> = {
  owner: 'nobody owns it',
  timeline: 'no target date',
  authority: 'no authority level',
  checkpoint: 'no review date',
}

export function delegationGapText(gaps: string[] | null | undefined): string | null {
  if (!gaps || gaps.length === 0) return null
  const words = gaps.map((g) => DELEGATION_GAP_LABEL[g] ?? g)
  if (words.length === 1) return `This delegation is incomplete: ${words[0]}.`
  return `This delegation is incomplete: ${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}.`
}

// Reading order of the board, and it is the order of a morning.
//
// Blocked sits second, above Ongoing, because a blocked task is waiting on
// somebody and is the only kind of work that cannot move itself. Open is now
// exactly "Not Started": since db/migrations/0023 the group follows the status,
// so the three situations that used to share the Open group have their own
// heading. Cancelled is last because it is an archive.
export const GROUP_ORDER: BoardGroup[] = [
  'Blocked',
  'Ongoing',
  'Open',
  'Standing',
  'On-Hold',
  'Completed',
  'Cancelled',
]

export const GROUP_LABEL: Record<BoardGroup, string> = {
  Blocked: 'Blocked',
  Ongoing: 'Ongoing',
  Open: 'Not started',
  Standing: 'Standing',
  'On-Hold': 'On hold',
  Completed: 'Completed',
  Cancelled: 'Cancelled',
}

// What the heading means, shown beside it, because "Open" used to mean three
// things and a renamed group with no explanation is how a team keeps using the
// old meaning.
export const GROUP_NOTE: Record<BoardGroup, string> = {
  Blocked: 'waiting on somebody or something',
  Ongoing: 'somebody is working on it',
  Open: 'nobody has started',
  Standing: 'recurring, and stays here until it is done or cancelled',
  'On-Hold': 'parked on purpose',
  Completed: '',
  Cancelled: '',
}

// The colour bar down the left of a group, as on the board this replaces.
export const GROUP_BAR: Record<BoardGroup, string> = {
  Blocked: 'bg-rose-500',
  Ongoing: 'bg-amber-500',
  Open: 'bg-sky-500',
  Standing: 'bg-violet-500',
  'On-Hold': 'bg-slate-500',
  Completed: 'bg-emerald-500',
  Cancelled: 'bg-slate-400',
}

/**
 * The group a status implies, mirroring public.task_group_for_status_v1.
 *
 * Used ONLY to tell the operator where a card is about to go before they change
 * its status. The database decides, this predicts, and the two are kept side by
 * side deliberately: if they ever disagree the board reloads and shows the
 * database's answer, because a figure is never computed in React.
 */
export function groupForStatus(status: TaskStatus, group: BoardGroup): BoardGroup {
  // Done and Cancelled are tested FIRST, and that ordering is the rule. Both
  // exemptions below apply to live work only, since db/migrations/0117: a
  // recurring task marked Done is not this week's run finished, it is somebody
  // saying the standing item itself is finished, and a killed card sitting in
  // the group the team reads every morning is the board saying something untrue.
  if (status === 'Done') return 'Completed'
  if (status === 'Cancelled') return 'Cancelled'
  if (group === 'Standing') return 'Standing'
  if (group === 'On-Hold') return 'On-Hold'
  if (status === 'Blocked') return 'Blocked'
  if (status === 'In Progress') return 'Ongoing'
  return 'Open'
}

/**
 * What the board may show once the server has confirmed a task edit.
 *
 * Built from `confirmed`, the row `updateTask` re-selects from v_task_board
 * after writing, NOT from what was requested. That distinction is the reason
 * this function takes both: on 23 August every jsonb patch write answered 200
 * and changed nothing, and a screen built from the request would have shown a
 * change that did not exist. A screen built from the returned row shows what
 * the database holds, which in that hour would have been the old value, which
 * is the correct and useful thing to show.
 *
 * Only fields the board row actually carries are taken, so the reload is left
 * to supply everything the client has no business asserting: is_open,
 * is_overdue, on_time, days_delta and group_status_conflict are decided by
 * v_task_board, per the rule at the top of this file. board_group comes from
 * the returned row too, so the card moves group only where the database moved
 * it.
 */
export function taskRowConfirmed(taskId: string, confirmed: Record<string, unknown> | null) {
  const fields: Record<string, unknown> = {}
  if (confirmed) for (const [key, value] of Object.entries(confirmed)) fields[key] = value
  return {
    label: 'task edit',
    routes: ['tasks/getTaskBoard', 'tasks/getTaskDetail'],
    match: (row: Record<string, unknown>) => row.id === taskId,
    set: (row: Record<string, unknown>) => {
      const next: Record<string, unknown> = {}
      for (const [key, value] of Object.entries(fields)) {
        if (key in row) next[key] = value
      }
      return next
    },
  }
}

/**
 * Fields that were asked for and did not come back changed.
 *
 * A write that answers 200 and changes nothing is not hypothetical here: it was
 * the live state of every jsonb patch in this cockpit for two days, and it was
 * invisible because the only symptom was the card sliding back after the
 * reload. `updateTask` returns the row it wrote, so the answer is checkable at
 * the moment it arrives, and an operator can be told plainly instead of being
 * left to wonder whether they clicked properly.
 *
 * Deliberately narrow. Only exact string fields are compared, because a date
 * sent as `2026-08-31` comes back as a timestamp and an array patch does not
 * map to a single column, and a false alarm about a save that worked would be
 * worse than no check at all.
 */
// authority_level joins the list because it has the same shape as the others: an
// exact string, written through the same jsonb patch, and setting it is a
// deliberate act somebody will want confirmed. The `key in confirmed` guard below
// means a bundle deployed before db/migrations/0085 reports nothing rather than
// crying wolf on every save.
const CHECKABLE = ['status', 'priority', 'title', 'board_group', 'authority_level'] as const

export function unappliedFields(
  patch: Record<string, unknown>,
  confirmed: Record<string, unknown> | null,
): string[] {
  if (!confirmed) return []
  const missed: string[] = []
  for (const key of CHECKABLE) {
    if (!(key in patch)) continue
    const asked = patch[key]
    if (typeof asked !== 'string') continue
    if (!(key in confirmed)) continue
    if (confirmed[key] !== asked) missed.push(key)
  }
  return missed
}

export const STATUS_CLASS: Record<TaskStatus, string> = {
  'Not Started': 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100',
  'In Progress': 'bg-amber-400 text-amber-950 dark:bg-amber-500 dark:text-amber-950',
  Blocked: 'bg-rose-500 text-white dark:bg-rose-600',
  Done: 'bg-emerald-500 text-white dark:bg-emerald-600',
  Cancelled: 'bg-slate-400 text-white dark:bg-slate-600',
}

export const PRIORITY_CLASS: Record<TaskPriority, string> = {
  High: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200',
  Medium: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  Low: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200',
}

/**
 * How to show on_time.
 *
 * The null case is the one that matters. A task with no target date has not
 * been delivered on time; it has not been measured, and the board must say so
 * rather than leaving a blank that reads as "fine". The word chosen is "No
 * target", which names the missing thing instead of the verdict.
 */
export function onTimeLabel(value: string | null): { text: string; tone: string } {
  switch (value) {
    case 'On Time':
      return { text: 'On time', tone: 'text-emerald-600 dark:text-emerald-400' }
    case 'Late':
      return { text: 'Late', tone: 'text-rose-600 dark:text-rose-400' }
    case 'Pending':
      return { text: 'Pending', tone: 'text-muted-foreground' }
    default:
      return { text: 'No target', tone: 'text-muted-foreground/60 italic' }
  }
}

/** Days early or late, in words. Null stays a dash, never a zero. */
export function daysDeltaLabel(value: number | null): string {
  if (value === null || value === undefined) return '—'
  if (value === 0) return 'on the day'
  const d = Math.abs(value)
  const unit = d === 1 ? 'day' : 'days'
  return value > 0 ? `${d} ${unit} late` : `${d} ${unit} early`
}

/**
 * The same figure for a narrow column, as a signed day count.
 *
 * Deliberately NOT the word again. The verdict column already says "Late", and
 * putting the words side by side produced "Late 12 late" on the board, which
 * reads like a bug even though the numbers were right.
 */
export function daysDeltaShort(value: number | null): string {
  if (value === null || value === undefined) return ''
  if (value === 0) return 'on the day'
  return value > 0 ? `+${value}d` : `${value}d`
}

/** 2026-08-22 as "22 Aug". Null renders as a dash, never as today. */
/**
 * The value an `<input type="date">` will actually accept, or '' if there is none.
 *
 * THE DEFECT THIS EXISTS TO STOP, found 26 August 2026 by opening the screen.
 *
 * `task.start_date` is a Postgres `date`. postgres.js parses it into a
 * JavaScript Date whatever `fetch_types` says, the Worker JSON.stringifies it,
 * and the browser receives "2026-06-29T00:00:00.000Z". `shortDate` copes with
 * that and renders "29 Jun", so every date on the board looked right.
 *
 * `<input type="date">` does not cope with it. Handed a full timestamp it
 * refuses the value and reports `.value === ''`. So clicking any date cell
 * opened an EMPTY picker, and the blur handler then compared '' to the stored
 * timestamp, found them different, and wrote null. Click a date, change your
 * mind, click away: the date was gone, with no message and no way to know.
 *
 * It had already happened in production. `task_activity` holds one
 * unexplained clear, target_completion 2026-07-10 to nothing on task 11,
 * "Call Netstar to remove tracker on Cindy's car", written on its own with no
 * status change beside it.
 *
 * Taking the ten characters before the T rather than re-deriving through a
 * local Date is deliberate: the timestamp names UTC midnight of the stored day,
 * and converting it into a local calendar day moves it backwards by one for
 * anybody west of Greenwich.
 *
 * The same helper is duplicated as `toDateInput` inside
 * components/fleet/EditVehicleDialog.tsx, which found this the hard way and
 * fixed it locally. That is a sign the real fix belongs at the API boundary,
 * where a `date` column would stop arriving as a timestamp at all. That is a
 * wider change than one screen and is not made here.
 */
export function dateInputValue(value: string | null): string {
  if (!value) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  const iso = /^(\d{4}-\d{2}-\d{2})T/.exec(value)
  return iso ? iso[1] : ''
}

export function shortDate(value: string | null): string {
  if (!value) return '—'
  const d = new Date(value + (value.length === 10 ? 'T00:00:00' : ''))
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' })
}

export function longDateTime(value: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Initials for the assignee bubble. "Deleted member" becomes DM, not a blank. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Stable per-name colour so the same person is the same colour on every row.
const BUBBLE_CLASSES = [
  'bg-sky-500',
  'bg-violet-500',
  'bg-emerald-600',
  'bg-amber-600',
  'bg-rose-500',
  'bg-teal-600',
  'bg-indigo-500',
]

export function bubbleClass(name: string): string {
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  return BUBBLE_CLASSES[h % BUBBLE_CLASSES.length]
}

/** Groups in reading order, each with its rows. Empty groups are dropped. */
export function groupRows(rows: TaskRow[]): { group: BoardGroup; rows: TaskRow[] }[] {
  const byGroup = new Map<BoardGroup, TaskRow[]>()
  for (const r of rows) {
    if (r.parent_task_id) continue
    const list = byGroup.get(r.board_group)
    if (list) list.push(r)
    else byGroup.set(r.board_group, [r])
  }
  return GROUP_ORDER.filter((g) => (byGroup.get(g)?.length ?? 0) > 0).map((g) => ({
    group: g,
    rows: byGroup.get(g) as TaskRow[],
  }))
}

/**
 * The person ids an update body actually names.
 *
 * THE TEXT IS THE SOURCE OF TRUTH, and that is the whole point of this
 * function. The picker knows who you clicked, but if you then delete the words
 * "@Kabelo Seboka" out of the sentence, Kabelo is not mentioned any more and
 * must not be emailed. Deriving the list from the body at post time means what
 * gets recorded is always what the author could see when they pressed Post.
 *
 * Matching is greedy and anchored at each '@'. Sorting longest-name-first and
 * using startsWith is not a micro-optimisation: with two people called "Jan"
 * and "Jan Smith", a naive `body.includes('@' + name)` check flags Jan for every
 * mention of Jan Smith, because "@Jan" is a substring of "@Jan Smith". At every
 * '@' the longest name that fits wins, so the sentence decides, not the list
 * order.
 *
 * People with no person_id are skipped. An assignee name with nobody behind it
 * is a real thing on this board (assignee_unlinked exists for it), and there is
 * nobody to notify.
 */
export function mentionIdsInBody(body: string, people: TaskPerson[]): string[] {
  const named = people
    .filter((p) => p.person_id && p.name.trim().length > 0)
    .sort((a, b) => b.name.length - a.name.length)
  const out = new Set<string>()
  for (let i = 0; i < body.length; i++) {
    if (body[i] !== '@') continue
    const rest = body.slice(i + 1)
    const hit = named.find((p) => rest.startsWith(p.name))
    if (hit && hit.person_id) out.add(hit.person_id)
  }
  return [...out]
}

/**
 * The partial name being typed at the caret, or null when the caret is not in a
 * mention. Drives the picker.
 *
 * Stops at a newline or a second '@' so a sentence that merely contains an
 * address does not open the picker halfway through it. Allows one space, because
 * these are full names and "Kabelo S" has to be reachable.
 */
export function mentionQueryAt(body: string, caret: number): { query: string; start: number } | null {
  const upto = body.slice(0, caret)
  const at = upto.lastIndexOf('@')
  if (at === -1) return null
  const between = upto.slice(at + 1)
  if (/[\n@]/.test(between)) return null
  // Two spaces means the person has moved on to writing prose.
  if ((between.match(/ /g) ?? []).length > 1) return null
  return { query: between, start: at }
}

// ---------------------------------------------------------------------------
// Date filtering
// ---------------------------------------------------------------------------
// Three windows, because the three dates answer three different questions:
// created is "when did this land on us", start is "when were we going to begin",
// target is "when did we promise it". One combined date picker would force the
// operator to remember which of the three the board was filtering by.
//
// The windows combine with AND. Asking for created in August and target in
// September means both, which is the reading that lets you find "things raised
// last month that we promised for next month". OR across three date columns has
// no question behind it that anybody actually asks.

export type TaskDateFilter = {
  createdFrom: string
  createdTo: string
  startFrom: string
  startTo: string
  targetFrom: string
  targetTo: string
  /**
   * Whether a task with no date in that column survives its filter.
   *
   * Both default to TRUE, and that is a safety decision rather than a
   * preference. On 26 August 2026, 243 of 434 tasks had no target completion and
   * 81 had no start date. A target window defaulting to false would silently
   * hide 56 percent of the board the moment somebody picked a date, and the
   * operator would have no way to tell a narrow window from a missing date. The
   * safe default shows more than you asked for, never less.
   *
   * There is no equivalent for created, because created_at is never null.
   */
  startIncludeUndated: boolean
  targetIncludeUndated: boolean
  /**
   * Whether Not Started, On-Hold and Blocked survive the Start and Target
   * windows whatever their dates.
   *
   * Defaults to TRUE for the same reason the two flags above do: the safe
   * default shows more than you asked for, never less. Measured on a target
   * window of 14 to 20 September 2026, ticked returned 448 rows and unticked
   * returned 323, so 125 rows of backlog turn on this one box.
   *
   * Untick it to ask the narrow question: only what actually falls in the
   * window, backlog included in the filtering.
   *
   * Created is never exempt either way. It answers "when did this land on us",
   * which is provenance rather than scheduling, and exempting it would put all
   * 78 dev plan cards into every "what arrived this week" view.
   */
  backlogIgnoresDates: boolean
}

/**
 * The work that is not moving.
 *
 * Kept in step by hand with BACKLOG_GROUPS in api/src/handlers/tasks/
 * getTaskBoard.ts. Two copies of a list is a real risk, so it is one line in
 * each and both carry this note; api/test/taskBoardOrder.test.ts asserts the
 * server's copy has not drifted.
 */
export const BACKLOG_GROUPS: BoardGroup[] = ['Open', 'On-Hold', 'Blocked']

export const EMPTY_DATE_FILTER: TaskDateFilter = {
  createdFrom: '',
  createdTo: '',
  startFrom: '',
  startTo: '',
  targetFrom: '',
  targetTo: '',
  startIncludeUndated: true,
  targetIncludeUndated: true,
  backlogIgnoresDates: true,
}

/**
 * How many of the three windows are set, for the chip on the toolbar.
 *
 * backlogIgnoresDates is deliberately NOT counted. It is not a fourth window,
 * it changes how the other two behave, and on its own it does nothing at all.
 * Counting it would put a (1) on the Dates button of a board that is not
 * filtered by any date, and a count that lies is worse than no count.
 */
export function activeDateFilterCount(f: TaskDateFilter): number {
  let n = 0
  if (f.createdFrom || f.createdTo) n++
  if (f.startFrom || f.startTo) n++
  if (f.targetFrom || f.targetTo) n++
  return n
}

/** Whether the backlog exemption is doing anything right now. */
export function backlogExemptionIsLive(f: TaskDateFilter): boolean {
  return (
    f.backlogIgnoresDates &&
    Boolean(f.startFrom || f.startTo || f.targetFrom || f.targetTo)
  )
}

export function dateFilterIsEmpty(f: TaskDateFilter): boolean {
  return activeDateFilterCount(f) === 0
}

/**
 * How many rows on the board are there ONLY because the backlog box is ticked.
 *
 * Computed here rather than guessed, because the number beside the checkbox is
 * the difference between "I know what this does" and "I will leave it alone".
 * It mirrors the server clause exactly: a row is held if it is in one of the
 * three groups AND it would fail the scheduling windows. Created is not part of
 * the test, because the exemption does not cover it.
 *
 * Counts top-level rows only, matching the count strip above the board.
 */
export function backlogHeldCount(rows: TaskRow[], f: TaskDateFilter): number {
  if (!backlogExemptionIsLive(f)) return 0

  const passes = (
    value: string | null,
    from: string,
    to: string,
    includeUndated: boolean,
  ): boolean => {
    if (!from && !to) return true
    if (value === null) return includeUndated
    // yyyy-mm-dd compares correctly as a string, which is why the board sends
    // and stores that shape everywhere rather than a Date.
    const d = value.slice(0, 10)
    if (from && d < from) return false
    if (to && d > to) return false
    return true
  }

  return rows.filter((r) => {
    if (r.parent_task_id) return false
    if (!BACKLOG_GROUPS.includes(r.board_group)) return false
    const inWindow =
      passes(r.start_date, f.startFrom, f.startTo, f.startIncludeUndated) &&
      passes(r.target_completion, f.targetFrom, f.targetTo, f.targetIncludeUndated)
    return !inWindow
  }).length
}

/**
 * The window as the handler wants it.
 *
 * An "include undated" flag is sent ONLY when its own window is set. Sending it
 * on its own would be meaningless, and sending false with no bounds would build
 * a `column is not null` clause that filters the board by nothing more than the
 * presence of a date, which is not what any of these controls claim to do.
 */
export function dateFilterParams(f: TaskDateFilter): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (f.createdFrom) out.createdFrom = f.createdFrom
  if (f.createdTo) out.createdTo = f.createdTo
  if (f.startFrom || f.startTo) {
    if (f.startFrom) out.startFrom = f.startFrom
    if (f.startTo) out.startTo = f.startTo
    out.startIncludeUndated = f.startIncludeUndated
  }
  if (f.targetFrom || f.targetTo) {
    if (f.targetFrom) out.targetFrom = f.targetFrom
    if (f.targetTo) out.targetTo = f.targetTo
    out.targetIncludeUndated = f.targetIncludeUndated
  }
  // Sent only when a scheduling window is set, for the same reason the two
  // undated flags are: on its own it means nothing, and sending false with no
  // bounds would build a clause that filters the board by nothing.
  if (f.startFrom || f.startTo || f.targetFrom || f.targetTo) {
    out.backlogIgnoresDates = f.backlogIgnoresDates
  }
  return out
}

// Where a task came from, for the chip on the board and the link in the panel.
// Arrives with db/migrations/0232.
//
// Returns null when the task was not raised by another board, which is most of
// them: a chip that says "task" on a task is noise, and the meeting chip beside
// it already has its own words.
export function originOf(row: {
  origin_kind?: string | null
  origin_url?: string | null
}): { label: string; url: string | null; tone: string } | null {
  switch (row.origin_kind) {
    case 'marketing':
      return {
        label: 'Marketing',
        url: row.origin_url ?? null,
        tone: 'bg-pink-100 text-pink-900 dark:bg-pink-950 dark:text-pink-200',
      }
    case 'tech':
      return {
        label: 'Dev plan',
        url: row.origin_url ?? null,
        tone: 'bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-200',
      }
    case 'dispute':
      return {
        label: 'CCMA',
        url: row.origin_url ?? null,
        tone: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
      }
    case 'content-gap':
      return {
        label: 'No content planned',
        url: row.origin_url ?? null,
        tone: 'bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200',
      }
    // 0617: a step on an offboarding case. It cannot be marked Done here, so the
    // link to the case is the way to finish it.
    case 'offboarding':
      return {
        label: 'Offboarding',
        url: row.origin_url ?? null,
        tone: 'bg-violet-100 text-violet-900 dark:bg-violet-950 dark:text-violet-200',
      }
    default:
      return null
  }
}
